import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseDeferredActionOptions {
  /** Pending 윈도우 길이(ms). 기본 3000. */
  duration?: number;
}

export interface UseDeferredActionReturn {
  /** 현재 pending 윈도우 활성 여부 */
  pending: boolean;
  /** 남은 초 (올림). 비-pending 시 duration/1000 */
  secondsLeft: number;
  /** Pending 윈도우 시작 — 만료 시 commitFn 호출. 이미 pending이면 무시 */
  trigger: () => void;
  /** Pending 취소 — commitFn 호출 안 함 */
  cancel: () => void;
}

/**
 * 클릭 자리에서 N초 lingering 후 commit하는 deferred action primitive.
 * 그 윈도우 안에 cancel() 호출 시 commitFn은 호출되지 않는다.
 *
 * 모달 confirm → toast undo → button-anchored pending action 패턴의 기반.
 */
export function useDeferredAction(
  commitFn: () => void,
  options: UseDeferredActionOptions = {}
): UseDeferredActionReturn {
  const { duration = 3000 } = options;
  const totalSeconds = Math.ceil(duration / 1000);

  const [pending, setPending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(totalSeconds);
  const commitTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const tickTimerRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const commitFnRef = useRef(commitFn);

  useEffect(() => {
    commitFnRef.current = commitFn;
  }, [commitFn]);

  const clearTimers = useCallback(() => {
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    commitTimerRef.current = undefined;
    tickTimerRef.current = undefined;
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const trigger = useCallback(() => {
    if (commitTimerRef.current) return;
    setPending(true);
    setSecondsLeft(totalSeconds);

    commitTimerRef.current = setTimeout(() => {
      clearTimers();
      setPending(false);
      commitFnRef.current();
    }, duration);

    tickTimerRef.current = setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
  }, [duration, totalSeconds, clearTimers]);

  const cancel = useCallback(() => {
    if (!commitTimerRef.current) return;
    clearTimers();
    setPending(false);
    setSecondsLeft(totalSeconds);
  }, [clearTimers, totalSeconds]);

  return { pending, secondsLeft, trigger, cancel };
}
